import React, { useEffect, useRef, useState } from "react";
import "./InAppNotifications.css";
import Avatar from "../base/Avatar";
import Icon from "../base/Icon";
import { useScreen } from "../../core/Global";


export interface InAppNotificationItem {
    id: string
    title: string
    body: string
    icon?: string
    // Where tapping the notification takes the user.
    link?: string
    // Stays until the user closes it instead of fading out on its own.
    keepOnScreen?: boolean
}

interface IProps {
    items: InAppNotificationItem[]
    onOpen: (item: InAppNotificationItem) => void
    onClose: (id: string) => void
}


const AUTO_CLOSE_MS = 6000

// How far up a card has to be dragged before letting go dismisses it.
const SWIPE_DISMISS_PX = 40


export default function InAppNotifications(props: IProps) {

    const screen = useScreen()

    if (props.items.length === 0) return null

    // Newest on top: these come down from the top of the screen, and the one that
    // just arrived is the one being read.
    const items = [...props.items].reverse()

    return (
        <div className="inapp-notifs">
            {
                items.map((item, index) =>
                    <InAppNotificationCard key={item.id} item={item} index={index} mobile={screen.isMobile}
                        onOpen={() => props.onOpen(item)}
                        onClose={() => props.onClose(item.id)} />
                )
            }
        </div>
    )
}


interface ICardProps {
    item: InAppNotificationItem
    // 0 is the newest card, the ones behind it sit slightly back in the stack.
    index: number
    mobile: boolean
    onOpen: () => void
    onClose: () => void
}

function InAppNotificationCard(props: ICardProps) {

    const [closing, closingSet] = useState<boolean>(false)
    const [dragY, dragYSet] = useState<number>(0)
    const [dragging, draggingSet] = useState<boolean>(false)

    const startY = useRef<number>(0)
    // A swipe ends in a click event too, so remember whether the finger moved.
    const swiped = useRef<boolean>(false)

    // A banner raised while the app is in the background has to still be there when
    // the user comes back, so the countdown only starts once the page is visible.
    useEffect(() => {

        if (props.item.keepOnScreen) return

        var timer: any

        const start = () => {
            if (timer) return
            timer = setTimeout(() => {
                closingSet(true)
                setTimeout(props.onClose, 200)
            }, AUTO_CLOSE_MS)
        }

        const visibilityChanged = () => {
            if (document.visibilityState == "visible") start()
        }

        if (document.visibilityState == "visible") start()
        else document.addEventListener("visibilitychange", visibilityChanged)

        return () => {
            clearTimeout(timer)
            document.removeEventListener("visibilitychange", visibilityChanged)
        }

    }, [props.item.id, props.item.keepOnScreen])

    // Play the exit first, then let the parent drop the item.
    const close = () => {
        closingSet(true)
        setTimeout(props.onClose, 200)
    }

    const touchStart = (e: React.TouchEvent) => {
        if (!props.mobile) return
        startY.current = e.touches[0].clientY
        swiped.current = false
        draggingSet(true)
    }

    // Downwards goes nowhere: this thing only leaves the way it came in.
    const touchMove = (e: React.TouchEvent) => {
        if (!props.mobile || !dragging) return
        const dy = e.touches[0].clientY - startY.current
        if (dy < -6) swiped.current = true
        dragYSet(Math.min(0, dy))
    }

    const touchEnd = () => {
        if (!props.mobile) return
        draggingSet(false)
        if (dragY <= -SWIPE_DISMISS_PX) close()
        else dragYSet(0)
    }

    const depth = Math.min(props.index, 2)
    const stacked = props.mobile && props.index > 0

    const style: React.CSSProperties = {
        zIndex: 100 - props.index,
        transition: dragging ? "none" : undefined
    }

    if (closing) {
        style.opacity = 0
        style.transform = props.mobile ? "translateY(-140%)" : "translateX(24px)"
    }
    else {
        // Only the phone stack leans back; on desktop they are a plain list.
        style.transform = `translateY(${dragY}px) scale(${props.mobile ? 1 - depth * 0.03 : 1})`
    }

    if (stacked) {
        style.marginTop = -8
        if (!closing) style.opacity = 1 - depth * 0.12
    }

    return (
        <div className="inapp-notif"
            style={style}
            onTouchStart={touchStart}
            onTouchMove={touchMove}
            onTouchEnd={touchEnd}
            onClick={() => {
                if (swiped.current) {
                    swiped.current = false
                    return
                }
                props.onOpen()
            }}>

            <Avatar className="inapp-notif-avatar" width={40} textSize={16}
                avatarUrl={props.item.icon} fullName={props.item.title} />

            <div className="inapp-notif-body">
                <div className="inapp-notif-title">{props.item.title}</div>
                <div className="inapp-notif-text">{props.item.body}</div>
            </div>

            {
                // On a phone the card is dismissed by flicking it up, so a close
                // button would only be one more thing in the way.
                !props.mobile &&
                <button type="button" className="inapp-notif-close"
                    onClick={e => {
                        e.stopPropagation()
                        close()
                    }}>
                    <Icon icon="xmark" size={14} />
                </button>
            }

        </div>
    )
}
